import { useState, useRef, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { ApiClientError, apiClient } from '../api/client';
import type { ChatMessage, ShopProfile } from '../types';
import './ChatPage.css';

// ─── 상수 ─────────────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const EXAMPLE_QUESTIONS = [
  '어제 매출 알려줘',
  '미처리 주문 있어?',
  '재고 부족 상품 확인해줘',
];

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

// ─── 유틸 ─────────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const dayName = DAY_NAMES[d.getDay()] ?? '';
  return `${year}년 ${month}월 ${day}일 (${dayName})`;
}

function getDateKey(iso: string): string {
  return new Date(iso).toLocaleDateString('ko-KR');
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ─── 타이핑 인디케이터 ────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="chat-message chat-message--assistant">
      <div className="chat-message__avatar" aria-hidden="true">🤖</div>
      <div className="chat-message__bubble">
        <div className="chat-message__typing" aria-label="응답 생성 중">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

// ─── 날짜 구분선 ──────────────────────────────────────────────────────────────

interface DateDividerProps {
  dateLabel: string;
}

function DateDivider({ dateLabel }: DateDividerProps) {
  return (
    <div className="chat-date-divider" role="separator">
      <span className="chat-date-divider__line" />
      <span className="chat-date-divider__label">{dateLabel}</span>
      <span className="chat-date-divider__line" />
    </div>
  );
}

// ─── 메시지 아이템 ────────────────────────────────────────────────────────────

interface MessageItemProps {
  message: ChatMessage;
  highlightKeyword?: string;
}

function MessageItem({ message, highlightKeyword }: MessageItemProps) {
  const isUser = message.role === 'user';

  const renderContent = (text: string) => {
    if (!highlightKeyword || !text) {
      return text.split('\n').map((line, i) => (
        <span key={i}>
          {i > 0 && <br />}
          {line}
        </span>
      ));
    }

    // 키워드 하이라이트
    const escapedKeyword = highlightKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const splitRegex = new RegExp(`(${escapedKeyword})`, 'gi');
    const testRegex = new RegExp(`^${escapedKeyword}$`, 'i');
    return text.split('\n').map((line, i) => {
      const parts = line.split(splitRegex);
      return (
        <span key={i}>
          {i > 0 && <br />}
          {parts.map((part, j) =>
            testRegex.test(part) ? (
              <mark key={j} className="chat-highlight">{part}</mark>
            ) : (
              part
            )
          )}
        </span>
      );
    });
  };

  return (
    <div className={`chat-message chat-message--${message.role}`}>
      {!isUser && (
        <div className="chat-message__avatar" aria-hidden="true">🤖</div>
      )}
      <div className="chat-message__body">
        <div className="chat-message__bubble">
          {message.content
            ? renderContent(message.content)
            : <span className="chat-message__empty">&#8203;</span>}
        </div>
        <time className="chat-message__time" dateTime={message.timestamp}>
          {formatTime(message.timestamp)}
        </time>
      </div>
    </div>
  );
}

// ─── 빈 상태 ──────────────────────────────────────────────────────────────────

interface EmptyStateProps {
  onExampleClick: (question: string) => void;
}

function EmptyState({ onExampleClick }: EmptyStateProps) {
  return (
    <div className="chat-page__empty">
      <div className="chat-page__empty-icon" aria-hidden="true">🤖</div>
      <h3 className="chat-page__empty-title">오팀장에게 무엇이든 물어보세요!</h3>
      <p className="chat-page__empty-desc">
        매출, 주문, 재고 등 쇼핑몰 운영에 관한 모든 것을 물어볼 수 있어요.
      </p>
      <div className="chat-page__examples">
        {EXAMPLE_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            className="chat-page__example-btn"
            onClick={() => onExampleClick(q)}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── 검색 바 ──────────────────────────────────────────────────────────────────

interface SearchBarProps {
  onSearch: (keyword: string) => void;
  onClose: () => void;
  isSearching: boolean;
}

function SearchBar({ onSearch, onClose, isSearching }: SearchBarProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed) {
      onSearch(trimmed);
    }
  };

  return (
    <form className="chat-search-bar" onSubmit={handleSubmit}>
      <svg className="chat-search-bar__icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
      </svg>
      <input
        ref={inputRef}
        type="text"
        className="chat-search-bar__input"
        placeholder="대화 검색..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={isSearching}
        aria-label="대화 검색"
      />
      {isSearching && (
        <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
      )}
      <button
        type="button"
        className="chat-search-bar__close"
        onClick={onClose}
        aria-label="검색 닫기"
      >
        ✕
      </button>
    </form>
  );
}

// ─── 입력 영역 ────────────────────────────────────────────────────────────────

interface InputAreaProps {
  onSend: (message: string) => void;
  disabled: boolean;
}

function InputArea({ onSend, disabled }: InputAreaProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    adjustHeight();
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="chat-page__input-area">
      <div className="chat-page__input-wrapper">
        <textarea
          ref={textareaRef}
          className="chat-page__textarea"
          placeholder="메시지를 입력하세요... (Enter로 전송, Shift+Enter로 줄바꿈)"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          aria-label="메시지 입력"
        />
        <button
          type="button"
          className="btn btn-primary chat-page__send-btn"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          aria-label="전송"
        >
          {disabled ? (
            <span className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
          ) : (
            <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          )}
        </button>
      </div>
      <p className="chat-page__input-hint">Enter로 전송 · Shift+Enter로 줄바꿈</p>
    </div>
  );
}

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

export default function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<ChatMessage[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const { data: shops } = useApi<ShopProfile[]>('/shops');
  const activeShopId = shops?.[0]?.id ? parseInt(shops[0].id, 10) : undefined;

  // 초기 로드: 최근 대화 히스토리
  useEffect(() => {
    void loadInitialHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadInitialHistory() {
    try {
      const data = await apiClient.get<{
        messages: Array<{ id: number; role: string; content: string; timestamp: string }>;
        hasMore: boolean;
      }>('/chat/history?limit=20');

      if (data.messages.length > 0) {
        setMessages(
          data.messages.map((m) => ({
            id: String(m.id),
            role: m.role as 'user' | 'assistant',
            content: m.content,
            timestamp: m.timestamp,
          }))
        );
        setHasMore(data.hasMore);
      }
    } catch {
      // 초기 로드 실패는 조용히 처리 — 빈 채팅으로 시작
    }
  }

  // 이전 대화 로드 (상단 스크롤)
  const loadOlderMessages = useCallback(async () => {
    if (isLoadingMore || !hasMore || messages.length === 0) return;

    const firstMessage = messages[0];
    if (!firstMessage) return;

    setIsLoadingMore(true);
    try {
      const data = await apiClient.get<{
        messages: Array<{ id: number; role: string; content: string; timestamp: string }>;
        hasMore: boolean;
      }>(`/chat/history?before=${encodeURIComponent(firstMessage.timestamp)}&limit=20`);

      if (data.messages.length > 0) {
        const olderMessages: ChatMessage[] = data.messages.map((m) => ({
          id: String(m.id),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp,
        }));
        // 스크롤 위치 보존: prepend 전 현재 높이를 기억하고, 추가 후 차이만큼 보정
        const container = messagesContainerRef.current;
        const prevScrollHeight = container?.scrollHeight ?? 0;
        setMessages((prev) => [...olderMessages, ...prev]);
        // React 상태 업데이트 후 DOM 반영까지 대기
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop += newScrollHeight - prevScrollHeight;
          }
        });
        setHasMore(data.hasMore);
      } else {
        setHasMore(false);
      }
    } catch {
      // 로드 실패 시 조용히 처리
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, messages]);

  // 상단 스크롤 감지
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop < 100 && hasMore && !isLoadingMore) {
        void loadOlderMessages();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, loadOlderMessages]);

  // 새 메시지가 추가될 때마다 하단으로 스크롤 (검색 중이 아닐 때만)
  useEffect(() => {
    if (!searchResults) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, searchResults]);

  // 언마운트 시 진행 중인 요청 중단
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // 검색 실행
  const handleSearch = useCallback(async (keyword: string) => {
    setIsSearching(true);
    setSearchKeyword(keyword);
    try {
      const data = await apiClient.get<{
        messages: Array<{ id: number; role: string; content: string; timestamp: string }>;
      }>(`/chat/search?q=${encodeURIComponent(keyword)}&limit=50`);

      setSearchResults(
        data.messages.map((m) => ({
          id: String(m.id),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp,
        }))
      );
    } catch {
      setError('검색 중 오류가 발생했습니다.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleCloseSearch = useCallback(() => {
    setShowSearch(false);
    setSearchResults(null);
    setSearchKeyword(null);
  }, []);

  const handleSend = useCallback(async (message: string) => {
    if (isStreaming) return;

    // 검색 모드 해제
    if (searchResults) {
      handleCloseSearch();
    }

    setError(null);

    // 사용자 메시지 추가
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsStreaming(true);

    // 커멘더 응답 메시지 (빈 상태로 먼저 추가)
    const assistantId = generateId();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    abortRef.current = new AbortController();

    try {
      const response = await fetch(`${BASE_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin', // otj_token 쿠키 자동 전송
        body: JSON.stringify({
          message,
          ...(activeShopId !== undefined ? { shopId: activeShopId } : {}),
        }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        let errMessage = `서버 오류 (${response.status})`;
        try {
          const errBody = await response.json() as { message?: string };
          if (errBody.message) errMessage = errBody.message;
        } catch {
          // 파싱 실패 시 기본 메시지 사용
        }
        if (response.status === 401) {
          errMessage = '세션이 만료되었습니다. 다시 로그인해주세요.';
        }
        throw new Error(errMessage);
      }

      // SSE 스트리밍 읽기
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('응답 스트림을 읽을 수 없습니다.');
      }

      const decoder = new TextDecoder();
      let accumulated = '';
      let isSSE: boolean | null = null; // SSE 여부를 첫 청크에서 판별

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // 첫 청크에서 SSE 형식 여부 판별
        if (isSSE === null) {
          isSSE = chunk.trimStart().startsWith('data:') || chunk.trimStart().startsWith(':');
        }

        if (isSSE) {
          // SSE 형식: "data: ..." 라인 파싱
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') break;
              accumulated += data;
            }
            // ":" 으로 시작하는 주석이나 빈 줄은 무시
          }
        } else {
          // Plain text 스트림: 청크를 직접 누적
          accumulated += chunk;
        }

        setMessages((prev) => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant') {
            updated[lastIdx] = { ...updated[lastIdx], content: accumulated };
          }
          return updated;
        });
      }

      if (!accumulated) {
        setMessages((prev) => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant') {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: '(응답이 없습니다.)',
            };
          }
          return updated;
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setMessages((prev) => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant' && !updated[lastIdx].content) {
            updated[lastIdx] = { ...updated[lastIdx], content: '(중단됨)' };
          }
          return updated;
        });
      } else {
        const errMsg =
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : '알 수 없는 오류가 발생했습니다.';
        setError(errMsg);
        setMessages((prev) => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant' && !updated[lastIdx].content) {
            return updated.slice(0, -1);
          }
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming, activeShopId, searchResults, handleCloseSearch]);

  // 렌더링할 메시지 결정 (검색 결과 vs 일반 대화)
  const displayMessages = searchResults ?? messages;

  // 날짜별 그룹화하여 구분선 삽입
  const renderMessages = () => {
    const elements: React.ReactElement[] = [];
    let lastDateKey = '';

    for (const msg of displayMessages) {
      const dateKey = getDateKey(msg.timestamp);
      if (dateKey !== lastDateKey) {
        elements.push(
          <DateDivider key={`date-${dateKey}`} dateLabel={formatDateLabel(msg.timestamp)} />
        );
        lastDateKey = dateKey;
      }
      elements.push(
        <MessageItem
          key={msg.id ?? `${msg.role}-${msg.timestamp}`}
          message={msg}
          highlightKeyword={searchKeyword ?? undefined}
        />
      );
    }

    return elements;
  };

  return (
    <div className="chat-page">
      {/* 커멘더 정보 바 */}
      <div className="chat-page__info-bar">
        <div className="chat-page__info-avatar" aria-hidden="true">🤖</div>
        <div className="chat-page__info-text">
          <span className="chat-page__info-name">오팀장 커멘더</span>
          <span className="chat-page__info-status">
            <span className="chat-page__status-dot" aria-hidden="true" />
            온라인
          </span>
        </div>
        <button
          type="button"
          className="chat-page__search-toggle"
          onClick={() => setShowSearch(!showSearch)}
          aria-label="대화 검색"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* 검색 바 */}
      {showSearch && (
        <SearchBar
          onSearch={handleSearch}
          onClose={handleCloseSearch}
          isSearching={isSearching}
        />
      )}

      {/* 검색 결과 안내 */}
      {searchResults && (
        <div className="chat-search-info">
          {searchResults.length > 0
            ? `"${searchKeyword}" 검색 결과 ${searchResults.length}건`
            : `"${searchKeyword}" 검색 결과가 없습니다.`}
        </div>
      )}

      {/* 메시지 영역 */}
      <div
        ref={messagesContainerRef}
        className="chat-page__messages"
        role="log"
        aria-live="polite"
        aria-label="채팅 메시지"
      >
        {/* 이전 대화 로드 버튼 */}
        {hasMore && !searchResults && (
          <div className="chat-page__load-more">
            {isLoadingMore ? (
              <span className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
            ) : (
              <button
                type="button"
                className="chat-page__load-more-btn"
                onClick={() => void loadOlderMessages()}
              >
                이전 대화 불러오기
              </button>
            )}
          </div>
        )}

        {displayMessages.length === 0 && !isStreaming ? (
          <EmptyState onExampleClick={(q) => void handleSend(q)} />
        ) : (
          <>
            {renderMessages()}
            {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
              <TypingIndicator />
            )}
          </>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="alert alert-error chat-page__error" role="alert">
            {error}
            <button
              type="button"
              className="chat-page__error-close"
              onClick={() => setError(null)}
              aria-label="오류 닫기"
            >
              ✕
            </button>
          </div>
        )}

        <div ref={messagesEndRef} aria-hidden="true" />
      </div>

      {/* 입력 영역 */}
      <InputArea onSend={(msg) => void handleSend(msg)} disabled={isStreaming} />
    </div>
  );
}
